import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const FAQ = () => {
  const faqs = [
    {
      category: "Booking & Payments",
      questions: [
        {
          q: "How do I book a transfer?",
          a: "Simply use our online booking form, select your pickup and drop-off locations, date, time, and number of passengers. You'll see an instant quote and can confirm your booking in minutes.",
        },
        {
          q: "Do I need to pay online?",
          a: "No, we don't require online payment. You pay your driver directly on arrival by card or cash. This ensures you only pay once your service is completed.",
        },
        {
          q: "Can I cancel my booking?",
          a: "Yes, cancellations can be made by informing us at least 24 hours in advance, and please contact us directly.",
        },
        {
          q: "Will I receive a confirmation?",
          a: "Yes, you'll receive an email confirmation immediately after booking with all your trip details and booking reference number.",
        },
      ],
    },
    {
      category: "Services & Vehicles",
      questions: [
        {
          q: "Are child seats available?",
          a: "Yes! We provide free child seats (infant carriers, toddler seats, and booster seats) upon request. Just mention your children's ages when booking.",
        },
        {
          q: "What vehicles do you use?",
          a: "We use premium vehicles including Mercedes E-Class sedans, and Mercedes V-Class vans. All vehicles are modern, air-conditioned, and well-maintained.",
        },
        {
          q: "How many passengers can you accommodate?",
          a: "Our vehicles can accommodate 1-8 passengers depending on the vehicle type. Sedans fit 1-3, and vans fit up to 8 passengers.",
        },
        {
          q: "Is there space for luggage?",
          a: "Yes, all our vehicles have ample luggage space. If you have extra-large items or special equipment, please mention this when booking.",
        },
      ],
    },
    {
      category: "Airport Transfers",
      questions: [
        {
          q: "Will my driver track my flight?",
          a: "Yes, we automatically track your flight status. If your flight is delayed, your driver will adjust their arrival time accordingly at no extra charge.",
        },
        {
          q: "Where will my driver meet me?",
          a: "For airport pickups, your driver will wait in the arrivals hall holding a sign with your name or at the pick-up point. We'll send you the relevant details including their contact details before pickup.",
        },
        {
          q: "What if my flight is early or delayed?",
          a: "We monitor all flights in real-time. We include 60 minutes of free waiting time for airport pickups, so delays are never a problem.",
        },
        {
          q: "Which airports do you serve?",
          a: "We serve all Paris airports: Charles de Gaulle (CDG), Paris Orly (ORY), and Beauvais-Tillé (BVA).",
        },
      ],
    },
    {
      category: "Disneyland Transfers",
      questions: [
        {
          q: "How long does it take to get to Disneyland?",
          a: "From CDG airport it's approximately 45 minutes, from Orly about 55 minutes, and from Paris city center around 45 minutes, depending on traffic.",
        },
        {
          q: "Can you drop us at our Disney hotel?",
          a: "Yes, we can drop you directly at any Disney hotel, partner hotel, or nearby accommodation. Just provide your hotel name when booking.",
        },
        {
          q: "Do you offer return transfers from Disneyland?",
          a: "Yes, we offer both one-way and return transfers.",
        },
      ],
    },
    {
      category: "Pricing",
      questions: [
        {
          q: "Are your prices fixed?",
          a: "Yes, our prices are fixed and include all taxes, tolls, and fees. The price you see is the price you pay - no hidden charges.",
        },
        {
          q: "Is there a night surcharge?",
          a: "Yes, a €15 flat surcharge applies for pickups between 10 PM and 6 AM.",
        },
        {
          q: "Do you charge for waiting time?",
          a: "Airport pickups include 60 minutes of free waiting time. Other pickups include 15 minutes. Additional waiting is charged at €0.50 per minute.",
        },
      ],
    },
  ];

  return (
    <div
      className="min-h-screen bg-paris-stone overflow-x-hidden"
      data-testid="faq-page"
    >
      {/* Hero */}
      <section className="bg-midnight-navy pt-24 pb-12 md:pt-28 md:pb-16">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-base md:text-lg text-paris-stone/80 max-w-2xl mx-auto">
              Everything you need to know about our transfer services
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-10 md:py-16 bg-paris-stone">
        <div className="container mx-auto px-4 max-w-3xl">
          {faqs.map((category, catIndex) => (
            <motion.div
              key={catIndex}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: catIndex * 0.1 }}
              className="mb-8 md:mb-10"
            >
              <h2 className="font-heading font-bold text-lg md:text-xl text-slate-900 mb-4">
                {category.category}
              </h2>
              <Accordion type="single" collapsible className="space-y-3">
                {category.questions.map((faq, qIndex) => (
                  <AccordionItem
                    key={qIndex}
                    value={`${catIndex}-${qIndex}`}
                    className="bg-white rounded-xl border border-slate-200 px-4 md:px-6"
                  >
                    <AccordionTrigger className="text-left font-medium text-slate-900 hover:text-eiffel-blue py-4 text-sm md:text-base">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600 pb-4 text-sm md:text-base">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-12 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-slate-900 mb-4">
            Still have questions?
          </h2>
          <p className="text-slate-600 mb-6 text-sm md:text-base">
            Our team is available 24/7 to help you
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 md:gap-4">
            <Button
              asChild
              className="bg-champagne-gold text-midnight-navy font-bold hover:bg-champagne-gold/90 rounded-full px-6 md:px-8"
            >
              <Link to="/contact">
                Contact Us
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="rounded-full px-6 md:px-8 hover:bg-midnight-navy hover:text-white"
            >
              <Link to="/booking">Book Now</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
